import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "src/store/store";
import { isAdminUser } from "src/utils/permissionUtil";
import * as S from "./styles";

// ----------------------------  SVG Icons  ----------------------------

const iconProps = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true as const,
};

/** Bullet-list icon (three rows: dot + line) */
const ListIcon: React.FC = () => (
  <svg {...iconProps}>
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

/** Microscope icon (workspace / review) */
const MicroscopeIcon: React.FC = () => (
  <svg {...iconProps}>
    <path d="M6 18h8" />
    <path d="M3 22h18" />
    <path d="M14 22a7 7 0 1 0 0-14h-1" />
    <path d="M9 14h2" />
    <path d="M9 12a2 2 0 0 1-2-2V6h6v4a2 2 0 0 1-2 2Z" />
    <path d="M12 6V3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3" />
  </svg>
);

/** Gear / cog icon (circle center + toothed ring) */
const SettingsIcon: React.FC = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

const NAV_ICONS: Record<string, React.FC> = {
  list: ListIcon,
  microscope: MicroscopeIcon,
  settings: SettingsIcon,
};

// ----------------------------  Nav Items  ----------------------------

interface NavItem {
  key: string;
  label: string;
  icon: string;
  path: string;
  matchPrefix: string;
  contextual?: boolean;
  badge?: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    key: "queue",
    label: "Audit Queue",
    icon: "list",
    path: "/audit/my-tasks",
    matchPrefix: "/audit/my-tasks|/audit/completed|/audit/expired",
  },
  {
    key: "workspace",
    label: "Audit Workspace",
    icon: "microscope",
    path: "/audit/workspace",
    matchPrefix: "/audit/workspace",
    contextual: true,
  },
  {
    key: "config",
    label: "Configuration",
    icon: "settings",
    path: "/audit/config",
    matchPrefix: "/audit/config",
    badge: "Admin",
  },
];

// ----------------------------  AuditLayout  ----------------------------

interface AuditLayoutProps {
  children: React.ReactNode;
  /** When set, the Workspace nav item links to this task and becomes active. */
  activeTaskId?: string;
}

const AuditLayout: React.FC<AuditLayoutProps> = ({ children, activeTaskId }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Configuration is admin-only; hide it from the sidebar for everyone else.
  const ldap = useAppSelector((state) => state.user.ldap);
  const isAdmin = isAdminUser(ldap);
  const visibleNavItems = NAV_ITEMS.filter((item) => item.key !== "config" || isAdmin);

  const hasActiveTask = Boolean(activeTaskId);
  const workspacePath = activeTaskId ? "/audit/workspace/" + activeTaskId : "/audit/workspace";

  const isActive = (item: NavItem): boolean => {
    const prefixes = item.matchPrefix.split("|");
    return prefixes.some((p) => location.pathname.startsWith(p));
  };

  // Special case: bare /audit path should highlight queue
  const isQueueDefault = location.pathname === "/audit" || location.pathname === "/audit/";

  return (
    <div style={S.auditLayoutWrapper}>
      {/* Sidebar */}
      <nav style={S.auditSidebar}>
        {visibleNavItems.map((item) => {
          const isWorkspace = item.contextual;
          const active = item.key === "queue" ? isActive(item) || isQueueDefault : isActive(item);

          // Contextual Workspace item: dimmed/disabled when no active task
          if (isWorkspace && !hasActiveTask) {
            return (
              <div
                key={item.key}
                style={sidebarDisabled}
                title="Opens when you claim or open a task"
              >
                {React.createElement(NAV_ICONS[item.icon] ?? ListIcon)}
                <span>{item.label}</span>
                <span style={contextualBadge}>Contextual</span>
              </div>
            );
          }

          const to = isWorkspace ? workspacePath : item.path;

          return (
            <button
              key={item.key}
              style={active ? S.sidebarLinkActive : S.sidebarLink}
              onClick={() => navigate(to)}
            >
              {React.createElement(NAV_ICONS[item.icon] ?? ListIcon)}
              <span>{item.label}</span>
              {item.badge && <span style={adminBadge}>{item.badge}</span>}
            </button>
          );
        })}
      </nav>

      {/* Main content area */}
      <div style={S.auditMainContent}>{children}</div>
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const adminBadge: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: "0.625rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  background: "rgba(30, 58, 138, 0.1)",
  color: "#1e3a8a",
  padding: "2px 6px",
  borderRadius: "4px",
};

const contextualBadge: React.CSSProperties = {
  marginLeft: "auto",
  fontSize: "0.625rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  background: "#f3f4f6",
  color: "#9ca3af",
  padding: "2px 6px",
  borderRadius: "4px",
};

const sidebarDisabled: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px 16px",
  margin: "0 8px",
  fontSize: "1rem",
  fontWeight: 500,
  color: "rgba(75, 85, 99, 0.4)",
  background: "none",
  border: "none",
  borderRadius: "6px",
  cursor: "not-allowed",
  width: "calc(100% - 16px)",
  textAlign: "left" as const,
  userSelect: "none" as const,
};

export default AuditLayout;
